import { Brands } from 'src/utils/enums/brands.enum';
import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DocumentType, Language } from 'src/utils/enums/documents.enum';
import {
    getBrandByDealerNumber,
    isBrandDucati,
    isSiteCanadian,
} from 'src/utils/helpers';

export interface FlatDocument {
    title: string;
    type: DocumentType;
    lang: string;
}

export interface GroupedDocument {
    group: string;
    list: (FlatDocument | GroupedDocument)[];
}

export type DocumentEntry = FlatDocument | GroupedDocument;

export interface TabConfig {
    key: string;
    label: string;
    documents: DocumentEntry[];
}

const removeEmptyTabs = (tabs: TabConfig[]): TabConfig[] =>
    tabs.filter((tab) => tab.documents.length > Number('0'));

export const useDocumentConfig = (): TabConfig[] => {
    const dealerDataInfo = useSelector((state: IUser) => state.dealerDataInfo);
    const { i18n } = useTranslation();
    const { documents } = useLocalization();
    const language = i18n.language.includes('fr') ? 'fr' : 'en';

    const { brand, dealerNumber } = dealerDataInfo?.dealer ?? {};
    const isDucati = dealerDataInfo
        ? isBrandDucati(getBrandByDealerNumber(brand!, dealerNumber!))
        : false;
    const showHandbook = dealerDataInfo ? !isDucati : false;

    const handbookEntry: FlatDocument | null = showHandbook
        ? {
              title: documents.DOCUMENTS_VIEW_NAMES_FINANCING,
              type: DocumentType.FINANCING_HANDBOOK,
              lang: language,
          }
        : null;

    if (isSiteCanadian()) {
        return removeEmptyTabs(
            getCanadaConfig(
                documents,
                language,
                handbookEntry,
                dealerDataInfo?.dealer.brand,
                isDucati,
            ),
        );
    }

    return removeEmptyTabs(
        getUSConfig(documents, language, handbookEntry, brand, isDucati),
    );
};

const getCanadaConfig = (
    documents: ReturnType<typeof useLocalization>['documents'],
    language: string,
    handbookEntry: FlatDocument | null,
    brand?: string,
    isDucati = false,
): TabConfig[] => [
    {
        key: 'GENERAL',
        label: documents.DOCUMENTS_VIEW_TABS_GENERAL,
        documents: [
            ...(handbookEntry ? [handbookEntry] : []),
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CERT,
                type: DocumentType.CERTIFICATION,
                lang: language,
            },
        ],
    },
    {
        key: 'ADVANCE_POLICIES',
        label: documents.DOCUMENTS_VIEW_TABS_POLICIES,
        documents: [
            {
                title: `${documents.DOCUMENTS_VIEW_TABS_POLICIES} - ${documents.DOCUMENTS_VIEW_NAMES_EN}`,
                type: DocumentType.ADVANCE_POLICY_FORM,
                lang: Language.EN,
            },
            {
                title: `${documents.DOCUMENTS_VIEW_TABS_POLICIES} - ${documents.DOCUMENTS_VIEW_NAMES_FR}`,
                type: DocumentType.ADVANCE_POLICY_FORM,
                lang: Language.FR,
            },
        ],
    },
    {
        key: 'CREDIT',
        label: documents.DOCUMENTS_VIEW_TABS_CREDIT,
        documents: [
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CCA,
                type: DocumentType.COMMERCIAL_CREDIT_APP,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CCAQ,
                type: DocumentType.COMMERCIAL_CREDIT_APP_QC,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CCAR,
                type: DocumentType.CONSUMER_CREDIT_APP,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CCAQC,
                type: DocumentType.CONSUMER_CREDIT_APP_QC,
                lang: language,
            },
        ],
    },
    {
        key: 'FUNDING',
        label: documents.DOCUMENTS_VIEW_TABS_FUNDING,
        documents: [
            ...(!isDucati
                ? [
                      {
                          group: documents.DOCUMENTS_VIEW_NAMES_WORKSHEETS,
                          list: [
                              {
                                  title:
                                      brand === Brands.VW
                                          ? documents.DOCUMENTS_VIEW_NAMES_VWSELECT
                                          : documents.DOCUMENTS_VIEW_NAMES_AUDISELECT,
                                  type: DocumentType.DRIVERS_OPTION,
                                  lang: language,
                              },
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_ONEPAY,
                                  type: DocumentType.ONE_PAY,
                                  lang: language,
                              },
                          ],
                      },
                  ]
                : []),
            {
                group: documents.DOCUMENTS_VIEW_NAMES_INSURANCE,
                list: [
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_EN,
                        type: DocumentType.INSUARANCE_INFO_VERIFICATION,
                        lang: Language.EN,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_FR,
                        type: DocumentType.INSUARANCE_INFO_VERIFICATION,
                        lang: Language.FR,
                    },
                ],
            },
            {
                group: documents.DOCUMENTS_VIEW_NAMES_PREFORM,
                list: [
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_EN,
                        type: DocumentType.PRE_AUTHORIZED_PAYMENT_FORM,
                        lang: Language.EN,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_FR,
                        type: DocumentType.PRE_AUTHORIZED_PAYMENT_FORM,
                        lang: Language.FR,
                    },
                ],
            },
            {
                group: documents.DOCUMENTS_VIEW_NAMES_CUSTOMER,
                list: [
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_EN,
                        type: DocumentType.CUSTOMER_IDENTIFICATION_FORM,
                        lang: Language.EN,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_FR,
                        type: DocumentType.CUSTOMER_IDENTIFICATION_FORM,
                        lang: Language.FR,
                    },
                ],
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_FUNDING_CHECKLIST,
                type: DocumentType.FUNDING_CHECKLIST,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CORPORATE,
                type: DocumentType.CDE_CORPORATE_RESOLUTION,
                lang: language,
            },
        ],
    },
    ...(!isDucati
        ? [
              {
                  key: 'LEASE_END',
                  label: documents.DOCUMENTS_VIEW_TABS_END,
                  documents: [
                      {
                          title: documents.DOCUMENTS_VIEW_NAMES_LPA,
                          type: DocumentType.LEASE_PULL_AHEAD_FORM,
                          lang: language,
                      },
                      {
                          title: documents.DOCUMENTS_VIEW_NAMES_REFUNDABLE,
                          type: DocumentType.REFUNDABLE_SECURITY_DEPOSIT,
                          lang: language,
                      },
                      {
                          title: documents.DOCUMENTS_VIEW_NAMES_LEASETERM,
                          type: DocumentType.LEASE_TERM_EXTENTION,
                          lang: language,
                      },
                  ],
              },
              {
                  key: 'LEASE_TRANSFER',
                  label: documents.DOCUMENTS_VIEW_TABS_TRANSFER,
                  documents: [
                      {
                          group: documents.DOCUMENTS_VIEW_NAMES_LEASEWORK,
                          list: [
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_EN,
                                  type: DocumentType.LEASE_TRANSFER_WORKSHEET,
                                  lang: Language.EN,
                              },
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_FR,
                                  type: DocumentType.LEASE_TRANSFER_WORKSHEET,
                                  lang: Language.FR,
                              },
                          ],
                      },
                      {
                          group: documents.DOCUMENTS_VIEW_NAMES_LEASECHECK,
                          list: [
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_EN,
                                  type: DocumentType.LEASE_TRANSFER_CHECKLIST,
                                  lang: Language.EN,
                              },
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_FR,
                                  type: DocumentType.LEASE_TRANSFER_CHECKLIST,
                                  lang: Language.FR,
                              },
                          ],
                      },
                  ],
              },
          ]
        : []),
];

const getUSConfig = (
    documents: ReturnType<typeof useLocalization>['documents'],
    language: string,
    handbookEntry: FlatDocument | null,
    brand?: string,
    isDucati = false,
): TabConfig[] => [
    {
        key: 'GENERAL',
        label: documents.DOCUMENTS_VIEW_TABS_GENERAL,
        documents: [
            ...(handbookEntry ? [handbookEntry] : []),
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CERT,
                type: DocumentType.CERTIFICATION,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_ORDER_SUPPLIES,
                type: DocumentType.ORDER_FORMS,
                lang: language,
            },
        ],
    },
    {
        key: 'CREDIT',
        label: documents.DOCUMENTS_VIEW_TABS_CREDIT,
        documents: [
            {
                title: documents.DOCUMENTS_VIEW_NAMES_BCA,
                type: DocumentType.BUSINESS_CREDIT_APP,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CONSUMER_CREDIT_US,
                type: DocumentType.CONSUMER_CREDIT_APP,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_EMPLOYMENT_VERIFICATION,
                type: DocumentType.EMPLOYMENT_VERIFICATION_AUTH,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_FOREIGN_BUSINESS,
                type: DocumentType.FOREIGN_BUSINESS_PROF_PROG_CHECKLIST,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_PERSONAL_REF,
                type: DocumentType.PERSONAL_REF_FORM,
                lang: language,
            },
        ],
    },
    {
        key: 'FUNDING',
        label: documents.DOCUMENTS_VIEW_TABS_FUNDING,
        documents: [
            {
                group: documents.DOCUMENTS_VIEW_NAMES_NOTICE_COSIGNER_FORMS,
                list: [
                    {
                        group: documents.DOCUMENTS_VIEW_NAMES_RETAIL,
                        list: [
                            {
                                title: 'Federal',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_FEDERAL,
                                lang: language,
                            },
                            {
                                title: 'California',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_CALIFORNIA,
                                lang: language,
                            },
                            {
                                title: 'Colorado',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_COLORADO,
                                lang: language,
                            },
                            {
                                title: 'Iowa',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_IOWA,
                                lang: language,
                            },
                            {
                                title: 'Illinois',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_ILLINOIS,
                                lang: language,
                            },
                            {
                                title: 'Notice to Guarantor - Illinois',
                                type: DocumentType.NOTICE_TO_GUARANTOR_RETAIL_ILLINOIS,
                                lang: language,
                            },
                            {
                                title: 'New York',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_NEWYORK,
                                lang: language,
                            },
                            {
                                title: 'North Carolina',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_NORTHCAROLINA,
                                lang: language,
                            },
                            {
                                title: 'Pennsylvania',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_PENNSYLVANIA,
                                lang: language,
                            },
                            {
                                title: 'South Carolina',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_SOUTHCAROLINA,
                                lang: language,
                            },
                            {
                                title: 'Texas',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_TEXAS,
                                lang: language,
                            },
                            {
                                title: 'Wisconsin',
                                type: DocumentType.NOTICE_TO_COSIGNER_RETAIL_WISCONSIN,
                                lang: language,
                            },
                        ],
                    },
                    ...(!isDucati
                        ? [
                              {
                                  group: documents.DOCUMENTS_VIEW_NAMES_LEASE,
                                  list: [
                                      {
                                          title: 'Federal',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_FEDERAL,
                                          lang: language,
                                      },
                                      {
                                          title: 'California',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_CALIFORNIA,
                                          lang: language,
                                      },
                                      {
                                          title: 'Colorado',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_COLORADO,
                                          lang: language,
                                      },
                                      {
                                          title: 'Iowa',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_IOWA,
                                          lang: language,
                                      },
                                      {
                                          title: 'North Carolina',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_NORTHCAROLINA,
                                          lang: language,
                                      },
                                      {
                                          title: 'Pennsylvania',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_PENNSYLVANIA,
                                          lang: language,
                                      },
                                      {
                                          title: 'South Carolina',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_SOUTHCAROLINA,
                                          lang: language,
                                      },
                                      {
                                          title: 'Texas',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_TEXAS,
                                          lang: language,
                                      },
                                      {
                                          title: 'Wisconsin',
                                          type: DocumentType.NOTICE_TO_COSIGNER_LEASE_WISCONSIN,
                                          lang: language,
                                      },
                                  ],
                              },
                          ]
                        : []),
                ],
            },
            ...(!isDucati
                ? [
                      {
                          group: documents.DOCUMENTS_VIEW_NAMES_EV_LEASE_ADDENDUM,
                          list: [
                              {
                                  title: 'Kansas',
                                  type: DocumentType.EV_LEASE_ADDENDUM_KANSAS,
                                  lang: language,
                              },
                              {
                                  title: 'Wisconsin',
                                  type: DocumentType.EV_LEASE_ADDENDUM_WISCONSIN,
                                  lang: language,
                              },
                              {
                                  title: 'Multi-State',
                                  type: DocumentType.EV_LEASE_ADDENDUM_MULTI_STATE,
                                  lang: language,
                              },
                          ],
                      },
                      {
                          group: documents.DOCUMENTS_VIEW_NAMES_EV_DSL_ADDENDUM,
                          list: [
                              {
                                  title: 'Multi-State',
                                  type: DocumentType.EV_DSL_ADDENDUM_MULTI_STATE,
                                  lang: language,
                              },
                              {
                                  title: 'California',
                                  type: DocumentType.EV_DSL_ADDENDUM_CA,
                                  lang: language,
                              },
                              {
                                  title: 'Illinois',
                                  type: DocumentType.EV_DSL_ADDENDUM_IL,
                                  lang: language,
                              },
                              {
                                  title: 'Missouri',
                                  type: DocumentType.EV_DSL_ADDENDUM_MO,
                                  lang: language,
                              },
                              {
                                  title: 'New York',
                                  type: DocumentType.EV_DSL_ADDENDUM_NY,
                                  lang: language,
                              },
                              {
                                  title: 'Utah',
                                  type: DocumentType.EV_DSL_ADDENDUM_UT,
                                  lang: language,
                              },
                              {
                                  title: 'Vermont',
                                  type: DocumentType.EV_DSL_ADDENDUM_VT,
                                  lang: language,
                              },
                              {
                                  title: 'Wisconsin',
                                  type: DocumentType.EV_DSL_ADDENDUM_WI,
                                  lang: language,
                              },
                          ],
                      },
                  ]
                : []),
            {
                group: documents.DOCUMENTS_VIEW_NAMES_FUNDING_OTHER,
                list: [
                    ...(!isDucati
                        ? [
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_EV_BUNDLE_ACK,
                                  type: DocumentType.EV_BUNDLE_ACK_FORM,
                                  lang: language,
                              },
                          ]
                        : []),
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_FUNDING_CHECKLIST,
                        type: DocumentType.FUNDING_CHECKLIST,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_CORPORATE,
                        type: DocumentType.CORPORATE_RESOLUTION,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_DEALER_RESERVE,
                        type: DocumentType.DEALER_RESERVE_DISCREPANCY_FORM,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_BONUS_RESEARCH,
                        type: DocumentType.BONUS_RESEARCH_REQUEST,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_LAW_PRINTING_ORDER,
                        type: DocumentType.LAW_PRINTING_ORDER_FORM,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_INSURANCE_FORM_US,
                        type: DocumentType.INSURANCE_FORM,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_ODOMETER,
                        type: DocumentType.ODOMETER_DISCLOSURE_STATEMENT,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_NM_INSURANCE_ALL_RETAILS,
                        type: DocumentType.NEW_MEXICO_INSURANCE_NOTICE_ALLRETAILS,
                        lang: language,
                    },
                    {
                        title: documents.DOCUMENTS_VIEW_NAMES_NM_INSURANCE_DEALER_AUTH,
                        type: DocumentType.NEW_MEXICO_INSURANCE_NOTICE_DEALERAUTH,
                        lang: language,
                    },
                    ...(brand === Brands.AUDI
                        ? [
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_AUDI_CARE,
                                  type: DocumentType.AUDI_CARE_FORM,
                                  lang: language,
                              },
                              {
                                  title: documents.DOCUMENTS_VIEW_NAMES_AUDI_CARE_CA,
                                  type: DocumentType.AUDI_CARE_FORM_CA,
                                  lang: language,
                              },
                          ]
                        : []),
                ],
            },
        ],
    },
    ...(!isDucati
        ? [
              {
                  key: 'PAYOFF',
                  label: documents.DOCUMENTS_VIEW_TABS_PAYOFF,
                  documents: [
                      {
                          title: documents.DOCUMENTS_VIEW_NAMES_SPEEDY_FAX,
                          type: DocumentType.SPEEDY_FAX,
                          lang: language,
                      },
                  ],
              },
          ]
        : []),
    {
        key: 'MISCELLANEOUS',
        label: documents.DOCUMENTS_VIEW_TABS_MISCELLANEOUS,
        documents: [
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CONSUMER_AUTO_PAYMENT,
                type: DocumentType.CONSUMER_AUTO_PAYMENT_FORM,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_AUTOPAYMENT_CANCEL,
                type: DocumentType.AUTOPAYMENT_CANCEL_FORM,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_SC_TAX_CREDIT,
                type: DocumentType.SOUTHCAROLINA_TAX_CREDIT_WORKSHEET_FORM,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_WI_TAX_CREDIT,
                type: DocumentType.WISONSIN_TAX_CREDIT_WORKSHEET_FORM,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_IL_INTERPRETER,
                type: DocumentType.IL_INTERPRETER_VERIFICATION,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_CT_TRADEIN,
                type: DocumentType.CTTRADE_INWORKSHEET,
                lang: language,
            },
            {
                title: documents.DOCUMENTS_VIEW_NAMES_ID_VERIFICATION,
                type: DocumentType.ID_VERIFICATION_FORM,
                lang: language,
            },
        ],
    },
];
